/**
 * Application Initialization State Machine
 *
 * Represents the various stages of application initialization,
 * from initial load through authentication to ready state.
 *
 * Genesis Portal State Flow:
 *   Uninitialized → Authenticating → Ready
 *   Error: Any state can transition to Error on failure
 *
 * Notes:
 * - Genesis Portal is simpler than NeoOne - no separate API token acquisition step
 * - No LoadingUserProfile step - roles come from B2C ID token claims
 * - No LoadingAccountDetails step - no feature flag loading required at init
 */
export enum AppInitState {
  /**
   * Initial state before any initialization has started
   */
  Uninitialized = 'Uninitialized',

  /**
   * Waiting for MSAL to finish processing any redirects
   * This is the state after APP_INITIALIZER runs but before we can check accounts
   */
  WaitingForMsal = 'WaitingForMsal',

  /**
   * Checking if user is authenticated with MSAL
   * Extracting user profile and roles from ID token claims
   */
  Authenticating = 'Authenticating',

  /**
   * Application is fully initialized and ready to use
   * User is authenticated with roles extracted from B2C claims
   */
  Ready = 'Ready',

  /**
   * An error occurred during initialization
   * User should be shown error message and retry option
   */
  Error = 'Error'
}

/**
 * User-friendly display messages for each state
 */
export const APP_INIT_STATE_MESSAGES: Record<AppInitState, string> = {
  [AppInitState.Uninitialized]: 'Initializing application...',
  [AppInitState.WaitingForMsal]: 'Connecting to authentication service...',
  [AppInitState.Authenticating]: 'Checking authentication...',
  [AppInitState.Ready]: 'Ready!',
  [AppInitState.Error]: 'An error occurred during initialization'
};

/**
 * Timeout values (in milliseconds) for each state transition
 */
export const APP_INIT_STATE_TIMEOUTS: Partial<Record<AppInitState, number>> = {
  [AppInitState.WaitingForMsal]: 30000, // 30 seconds - MSAL redirect handling
  [AppInitState.Authenticating]: 30000 // 30 seconds - authentication check
};

/**
 * Custom error type to signal that user needs to login
 * This is NOT a failure state - it's expected for unauthenticated users
 */
export class NeedLoginError extends Error {
  constructor(message: string = 'User needs to authenticate') {
    super(message);
    this.name = 'NeedLoginError';
    Object.setPrototypeOf(this, NeedLoginError.prototype);
  }
}
